import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDrag } from "react-dnd";
import { flushSync } from "react-dom";
import { API } from "aws-amplify";
import swal from "@sweetalert/with-react";

import {
  sortArrayOfObjects,
  capitalizeFirstLetters,
  calculateConflicts,
} from "../../../../js/Helpers";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faUnlock,
  faChalkboard,
  faUserGraduate,
  faEllipsisH,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import Loader from "../../common/Loader";
import useMagnetboardStore, { useConflictActions, useLoadingData } from "../../store/magnetboardStore";
import { getSectionSingletonDoubletonInfo } from "../utils/sectionClassification";
import {
  buildClassroomAvailabilityContext,
  isClassroomAvailableForSection,
} from "../utils/classroomAvailability";
import { getLabelColorClass, INCLUSION_LABEL_COLOR } from "../../utils/labelColorUtils";
import { filterOutConflicts } from "../../../../js/Conflicts";
import loading_gif from "../../../../images/balls.gif";

const QUARTERS = ["1", "2", "3", "4"];
const CYCLE_QUARTERS = ["1", "2", "3", "4", "5", "6"];
const INCLUSION_SUBSECTION_TEXT_CLASS = `${INCLUSION_LABEL_COLOR}-text`;

const sectionHasCycleQuarterDays = (section_info = {}) =>
  (Array.isArray(section_info.quarter_days) ? section_info.quarter_days : []).some((quarter_day) =>
    ["5", "6"].includes(`${quarter_day.quarter}`)
  );

const getQuarterDisplayInfo = (quarter_days = [], display_quarters = QUARTERS) => {
  const quarter_set = new Set((Array.isArray(quarter_days) ? quarter_days : []).map((quarter_day) => `${quarter_day.quarter}`));
  const quarter_selected_lookup = display_quarters.reduce((lookup, quarter) => {
    lookup[quarter] = quarter_set.has(`${quarter}`);
    return lookup;
  }, {});
  return {
    quarter1Bool: quarter_set.has("1"),
    quarter2Bool: quarter_set.has("2"),
    quarter3Bool: quarter_set.has("3"),
    quarter4Bool: quarter_set.has("4"),
    quarter5Bool: quarter_set.has("5"),
    quarter6Bool: quarter_set.has("6"),
    quarterSelectedLookup: quarter_selected_lookup,
    displayQuarters: display_quarters,
    quarterLabelPrefix: display_quarters.length > 4 ? "C" : "Q",
    quarterKey: display_quarters.filter((quarter) => quarter_set.has(quarter)).join("|"),
  };
};

const MagnetboardSection = React.memo(
  ({
    schoolInfo,
    scheduleInfo,
    scheduleStructureData,
    scheduleType,
    currentViewType,
		currentView,
    currentDay,
    currentScheduleVersionID,
    teachers,
    courses,
    classrooms,
    sections,
    setSections,
    subsections,
    labels,
    setConflictsLoading,
    labsOnlyFiltered,
    nonLabsOnlyFiltered,
    singletonsFiltered,
    doubletonsFiltered,
    noClassroomFiltered,
    hasClassroomFiltered,
    toggleEditSection,
    updateSectionPlacement,
    duplicateSectionFromMagnetboard,
    deleteSectionFromMagnetboard,
    setDuplicateLoadingTarget,
    deleteLoadingTargets,
    setDeleteLoadingTarget,
    dataID,
    coursePeriodID,
    sectionID,
    studentID,
    sectionType,
    handleSelectQuickViewDataID = () => {},
    selectedQuickViewSectionID,
    toggleLockSection,
    isDraggable,
		isEditable,
    isClassroomEditable = false,
    isLockable,
    isQuickViewEnabled,
    isPrintMode = false,
    printDisplayOptions = null,
    studentConflictSectionLookup,
    sectionConflictSummaryBySectionId,
    magnetboardOptions = {},
  }) => {
    const [sectionActionsOpen, setSectionActionsOpen] = useState(false);
    const [classroomMenuOpen, setClassroomMenuOpen] = useState(false);
    const [classroomSaving, setClassroomSaving] = useState(false);
    const [isDuplicatingSection, setIsDuplicatingSection] = useState(false);
    const [displayQuarterDaysBySectionId, setDisplayQuarterDaysBySectionId] = useState({});
    const [quarterLoadingKeys, setQuarterLoadingKeys] = useState([]);
    const sectionActionsRef = useRef(null);
    const classroomMenuRef = useRef(null);
    const componentMountedRef = useRef(true);
    const sectionQuarterDaysRef = useRef(null);
    const queuedQuarterPayloadRef = useRef(null);
    const quarterBatchOriginalStateRef = useRef(null);
    const quarterSaveTimeoutRef = useRef(null);
    const quarterSaveInFlightRef = useRef(false);
    const { isLockSectionLoading, isLockSectionIds } = useLoadingData();

    const { setConflicts, markConflictDataFresh } = useConflictActions();

    useEffect(() => {
      componentMountedRef.current = true;
      return () => {
        componentMountedRef.current = false;
      };
    }, []);

    const cloneQuarterDays = (quarter_days = []) =>
      (Array.isArray(quarter_days) ? quarter_days : []).map((quarter_day) => ({
        ...quarter_day,
      }));

    const getQuarterLoadingKey = (target_section_id, quarter) => `${target_section_id}:${quarter}`;

    const getDisplayQuarterDaysForSection = useCallback((target_section_id, fallback_quarter_days = []) => {
      const target_key = `${target_section_id}`;
      return Object.prototype.hasOwnProperty.call(displayQuarterDaysBySectionId, target_key)
        ? displayQuarterDaysBySectionId[target_key]
        : fallback_quarter_days;
    }, [displayQuarterDaysBySectionId]);

    const markQuarterLoading = (target_section_id, quarter) => {
      if (!target_section_id || !quarter) return;
      const loading_key = getQuarterLoadingKey(target_section_id, quarter);
      setQuarterLoadingKeys((current_keys) =>
        current_keys.includes(loading_key)
          ? current_keys
          : [...current_keys, loading_key]
      );
    };

    const clearQuarterLoading = () => {
      setQuarterLoadingKeys([]);
    };

    useEffect(() => {
      if (!sectionActionsOpen) return;

      const handleOutsideClick = (event) => {
        if (sectionActionsRef.current?.contains(event.target)) return;
        setSectionActionsOpen(false);
      };

      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("touchstart", handleOutsideClick);

      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
        document.removeEventListener("touchstart", handleOutsideClick);
      };
    }, [sectionActionsOpen]);

    useEffect(() => {
      if (!classroomMenuOpen) return;

      const handleOutsideClick = (event) => {
        if (classroomMenuRef.current?.contains(event.target)) return;
        setClassroomMenuOpen(false);
      };

      document.addEventListener("mousedown", handleOutsideClick);
      document.addEventListener("touchstart", handleOutsideClick);

      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
        document.removeEventListener("touchstart", handleOutsideClick);
      };
    }, [classroomMenuOpen]);

    ///////////////////////////
    ///// OTHER FUNCTIONS /////
    ///////////////////////////
    const scheduleQuarterDaySave = () => {
      if (quarterSaveTimeoutRef.current) clearTimeout(quarterSaveTimeoutRef.current);
      quarterSaveTimeoutRef.current = setTimeout(async () => {
        if (quarterSaveInFlightRef.current || !queuedQuarterPayloadRef.current) return;

        const payload = queuedQuarterPayloadRef.current;
        queuedQuarterPayloadRef.current = null;
        quarterSaveInFlightRef.current = true;

        const apiName = process.env.REACT_APP_ENDPOINT_NAME;
        const url = "/update-section-quarter-days";
        const myInit = {
          response: true,
          body: JSON.stringify({
            school_id: schoolInfo.school_id,
            schedule_version_id: currentScheduleVersionID,
            affected_sections: payload.affected_sections,
            new_quarter_days: payload.new_quarter_days,
            allow_separate_subsection_update: payload.allow_separate_subsection_update,
          }),
        };

        try {
          await API.post(apiName, url, myInit);
        } catch (error) {
          const original_quarter_days = quarterBatchOriginalStateRef.current || {};
          sectionQuarterDaysRef.current = {};
          setDisplayQuarterDaysBySectionId((current_display_quarter_days) => {
            const next_display_quarter_days = { ...current_display_quarter_days };
            Object.keys(original_quarter_days).forEach((affected_section_id) => {
              delete next_display_quarter_days[`${affected_section_id}`];
            });
            return next_display_quarter_days;
          });
          queuedQuarterPayloadRef.current = null;
          quarterBatchOriginalStateRef.current = null;
          quarterSaveInFlightRef.current = false;
          clearQuarterLoading();

          swal({
            title: "Unable to Update Quarters",
            icon: "warning",
            buttons: {
              cancel: {
                text: "Okay",
                value: false,
                visible: true,
                className: "blue-btn",
              },
            },
            content: (
              <div>
                <p>{error?.response?.data?.msg || error?.response?.data?.message || "There was a problem updating this section's quarters."}</p>
              </div>
            ),
          });
          return;
        }

        quarterSaveInFlightRef.current = false;

        if (queuedQuarterPayloadRef.current) {
          scheduleQuarterDaySave();
          return;
        }

        const affectedSectionIDs = new Set(
          (Array.isArray(payload.affected_sections) ? payload.affected_sections : []).map(
            (affected_section_id) => `${affected_section_id}`
          )
        );

        setSections((current_sections) =>
          current_sections.map((current_section_info) => {
            if (!affectedSectionIDs.has(`${current_section_info.section_id}`)) return current_section_info;
            const current_section_is_subsection = subsections.some(
              (subsection_info) => `${subsection_info.subsection_id}` === `${current_section_info.section_id}`
            );
            if (
              current_section_is_subsection &&
              `${current_section_info.has_separate_quarter_days || "1"}` === "1" &&
              payload.allow_separate_subsection_update !== true
            ) {
              return current_section_info;
            }

            return {
              ...current_section_info,
              quarter_days: cloneQuarterDays(payload.new_quarter_days),
            };
          })
        );
        quarterBatchOriginalStateRef.current = null;
        setDisplayQuarterDaysBySectionId((current_display_quarter_days) => {
          const next_display_quarter_days = { ...current_display_quarter_days };
          affectedSectionIDs.forEach((affected_section_id) => {
            delete next_display_quarter_days[`${affected_section_id}`];
          });
          return next_display_quarter_days;
        });
        sectionQuarterDaysRef.current = {};
        clearQuarterLoading();
        setConflictsLoading(true);

        try {
          const conflicts_to_check = [
            "teacher_double_booked",
            "classroom_double_booked",
            "student_double_booked",
            "teacher_conflicting_periods",
            "classroom_conflicting_periods",
            "student_conflicting_periods",
            "teacher_too_many_sections",
            "teacher_too_many_sections_in_a_row",
          ];
          const conflicts_response = await calculateConflicts({
            school_id: schoolInfo.school_id,
            schedule_version_id: schoolInfo.current_schedule_version_id,
            conflicts_to_check: conflicts_to_check,
          });
          const conflicts_list = conflicts_response.conflicts;
          const current_conflicts = useMagnetboardStore.getState().conflicts || [];
          const filtered_out_conflicts = filterOutConflicts(current_conflicts, [], conflicts_to_check);
          setConflicts([...filtered_out_conflicts, ...conflicts_list]);
          markConflictDataFresh(schoolInfo.school_id, schoolInfo.current_schedule_version_id);
        } finally {
          setConflictsLoading(false);
        }
      }, 90);
    };

    const toggleSectionQuarterDay = (target_section_id, quarter, { allow_separate_subsection_update = false } = {}) => {
      const section_index = sections.findIndex(
        (section) => `${section.section_id}` === `${target_section_id}`
      );
      if (section_index === -1) return;

      const target_section_info = sections[section_index];
      const section_subsections = Array.isArray(target_section_info.subsections) ? target_section_info.subsections : [];
      const current_quarter_days = cloneQuarterDays(
        sectionQuarterDaysRef.current?.[`${target_section_id}`] || target_section_info.quarter_days
      );
      const section_span_id = target_section_info.span_id;
      const section_is_lab = target_section_info.is_lab;

      let affected_sections;
      if (allow_separate_subsection_update) {
        affected_sections = [target_section_id];
      } else if (section_span_id) {
        affected_sections = [];

        for (const section of sections) {
          if (section.span_id === section_span_id && section.is_lab === section_is_lab) {
            affected_sections.push(section.section_id);
            if (Array.isArray(section.subsections) && section.subsections.length > 0) {
              affected_sections.push(...section.subsections);
            }
          }
        }
      } else {
        affected_sections = section_subsections.length > 0
          ? [target_section_id, ...section_subsections]
          : [target_section_id];
      }

      if (!quarterBatchOriginalStateRef.current) quarterBatchOriginalStateRef.current = {};
      affected_sections.forEach((affected_section_id) => {
        if (!Object.prototype.hasOwnProperty.call(quarterBatchOriginalStateRef.current, `${affected_section_id}`)) {
          const affected_section_info = sections.find(
            (section) => `${section.section_id}` === `${affected_section_id}`
          );
          quarterBatchOriginalStateRef.current[affected_section_id] = cloneQuarterDays(affected_section_info?.quarter_days);
        }
      });

      const quarter_index = current_quarter_days.findIndex(
        (quarter_day) => quarter_day.quarter === quarter
      );

      let new_quarter_days = [];
      if (quarter_index !== -1) {
        new_quarter_days = current_quarter_days.filter(
          (quarter_day) => quarter_day.quarter !== quarter
        );
      } else {
        const unique_days = current_quarter_days.length === 0
          ? Array.from({ length: parseInt(scheduleInfo.num_days_in_cycle) }, (_, i) => `${i + 1}`)
          : [...new Set(current_quarter_days.map((quarter_day) => `${quarter_day.day}`))];

        const additional_quarter_days = unique_days.map((day) => ({ day: `${day}`, quarter }));
        new_quarter_days = [...current_quarter_days, ...additional_quarter_days];
      }

      sectionQuarterDaysRef.current = {
        ...(sectionQuarterDaysRef.current || {}),
        [`${target_section_id}`]: cloneQuarterDays(new_quarter_days),
      };
      setDisplayQuarterDaysBySectionId((current_display_quarter_days) => ({
        ...current_display_quarter_days,
        [`${target_section_id}`]: cloneQuarterDays(new_quarter_days),
      }));
      queuedQuarterPayloadRef.current = {
        affected_sections,
        new_quarter_days: cloneQuarterDays(new_quarter_days),
        allow_separate_subsection_update,
      };

      if (!quarterSaveInFlightRef.current) scheduleQuarterDaySave();
    };

		const section_id = useMemo(() => {
			const section_with_subsection_index = subsections.findIndex(
        (subsection_section) => subsection_section.subsection_id === sectionID
      );
			return section_with_subsection_index !== -1
          ? subsections[section_with_subsection_index].section_id
          : sectionID
		}, [sectionID, subsections]);

    const subsectionParentById = useMemo(() => {
      return subsections.reduce((results, subsection_info) => {
        results.set(`${subsection_info.subsection_id}`, `${subsection_info.section_id}`);
        return results;
      }, new Map());
    }, [subsections]);

		// Optimized: Break down computedData into smaller memos
		const sectionInfo = useMemo(() => {
			return sections.find((section) => section.section_id === section_id) || {
				section_number: null,
				section_type: "1",
				section_id: null,
				course_id: null,
				course_periods: [],
				classroom_id: null,
				is_manual: "0",
				is_lab: "0",
				subsections: [],
			};
		}, [sections, section_id]);

    const affectedMainSections = useMemo(() => {
      if (!sectionInfo.section_id) return [];

      const span_id = sectionInfo.span_id;
      const base_sections = span_id
        ? sections.filter(
            (section) =>
              `${section.span_id || ""}` === `${span_id}`
          )
        : [sectionInfo];

      const logged_main_section_ids = new Set();
      const results = [];

      base_sections.forEach((section) => {
        const main_section_id = subsectionParentById.get(`${section.section_id}`) || `${section.section_id}`;
        if (logged_main_section_ids.has(main_section_id)) return;

        const main_section_info = sections.find(
          (current_section) => `${current_section.section_id}` === `${main_section_id}`
        );
        if (!main_section_info) return;

        logged_main_section_ids.add(main_section_id);
        results.push(main_section_info);
      });

      return results;
    }, [sectionInfo, sections, subsectionParentById]);

    const affectedSectionPartIds = useMemo(() => {
      const section_part_ids = [];
      affectedMainSections.forEach((main_section_info) => {
        if (!main_section_info?.section_id) return;
        section_part_ids.push(`${main_section_info.section_id}`);
        (Array.isArray(main_section_info.subsections) ? main_section_info.subsections : []).forEach((subsection_id) => {
          section_part_ids.push(`${subsection_id}`);
        });
      });
      return [...new Set(section_part_ids)];
    }, [affectedMainSections]);

    const allSectionPartsLocked = useMemo(() => {
      if (affectedSectionPartIds.length === 0) return sectionInfo.locked === "1";
      return affectedSectionPartIds.every((section_part_id) => {
        const section_part_info = sections.find((section) => `${section.section_id}` === `${section_part_id}`);
        return section_part_info?.locked === "1";
      });
    }, [affectedSectionPartIds, sections, sectionInfo.locked]);

    useEffect(() => {
      if (quarterSaveInFlightRef.current || queuedQuarterPayloadRef.current) return;
      sectionQuarterDaysRef.current = {};
      setDisplayQuarterDaysBySectionId({});
    }, [sectionInfo.quarter_days]);

    useEffect(() => {
      return () => {
        if (quarterSaveTimeoutRef.current) clearTimeout(quarterSaveTimeoutRef.current);
      };
    }, []);

		const teacherInfo = useMemo(() => {
			const teacher_id = sectionInfo.teacher_id;
			return teachers.find(teacher => teacher.teacher_id === teacher_id) || {
				name: null,
				first_name: null,
			};
		}, [teachers, sectionInfo.teacher_id]);

		const courseAndClassroomInfo = useMemo(() => {
			const course_id = sectionInfo.course_id;
			const classroom_id = sectionInfo.classroom_id;
			const section_type = sectionInfo.section_type;
			
			const course_info = courses.find((course) => course.course_id === course_id);
			const course_name = section_type === "2"
				? "Prep"
				: section_type === "3"
				? "Duty"
				: course_info
				? `${capitalizeFirstLetters(course_info.name)} (${course_info.course_code})`
				: null;
			
			const classroom_info = classrooms.find((classroom) => classroom.classroom_id === classroom_id);
			const classroom_name = classroom_info ? classroom_info.classroom_name : null;
			
			return { course_name, classroom_name };
		}, [courses, classrooms, sectionInfo.course_id, sectionInfo.classroom_id, sectionInfo.section_type]);

    const classroomAvailabilityContext = useMemo(() => {
      if (!classroomMenuOpen) return null;
      return buildClassroomAvailabilityContext({
        sectionInfo,
        sections,
        scheduleInfo,
        scheduleStructureData,
      });
    }, [classroomMenuOpen, scheduleInfo, scheduleStructureData, sectionInfo, sections]);

    const classroomOptions = useMemo(() => {
      if (!classroomMenuOpen || !classroomAvailabilityContext) {
        return { attached: [], other: [] };
      }

      const course_info = courses.find(
        (course) => `${course.course_id}` === `${sectionInfo.course_id}`
      );
      const course_classroom_ids = new Set(
        (Array.isArray(course_info?.classroom_restrictions)
          ? course_info.classroom_restrictions
          : []
        ).map((classroom_id) => `${classroom_id}`)
      );
      const teacher_classroom_ids = new Set(
        (Array.isArray(teacherInfo?.classroom_restrictions)
          ? teacherInfo.classroom_restrictions
          : []
        ).map((classroom_id) => `${classroom_id}`)
      );

      const options = classrooms
        .map((classroom_info) => {
          const classroom_id = `${classroom_info.classroom_id}`;
          const attached_to_course = course_classroom_ids.has(classroom_id);
          const attached_to_teacher = teacher_classroom_ids.has(classroom_id);

          return {
            classroomInfo: classroom_info,
            attachedToCourse: attached_to_course,
            attachedToTeacher: attached_to_teacher,
            available: isClassroomAvailableForSection({
              classroomInfo: classroom_info,
              sections,
              availabilityContext: classroomAvailabilityContext,
            }),
          };
        })
        .sort((left, right) =>
          `${left.classroomInfo.classroom_name || ""}`.localeCompare(
            `${right.classroomInfo.classroom_name || ""}`,
            undefined,
            { numeric: true, sensitivity: "base" }
          )
        );

      return {
        attached: options.filter(
          (option) => option.attachedToCourse || option.attachedToTeacher
        ),
        other: options.filter(
          (option) => !option.attachedToCourse && !option.attachedToTeacher
        ),
      };
    }, [
      classroomAvailabilityContext,
      classroomMenuOpen,
      classrooms,
      courses,
      sectionInfo.course_id,
      sections,
      teacherInfo,
    ]);

		const studentCount = useMemo(() => {
			const student_list = "student_list" in sectionInfo ? sectionInfo.student_list : [];
			const section_subsections = "subsections" in sectionInfo
				? sectionInfo.subsections.filter((subsection) =>
						sections.findIndex((section) => section.section_id === subsection) !== -1
					)
				: [];

			return section_subsections.length === 0
				? student_list.length
				: section_subsections.reduce((total_students, subsection_id) => {
						const subsection_index = sections.findIndex((section) => section.section_id === subsection_id);
						if (subsection_index === -1) return total_students;
						const subsection_student_list = "student_list" in sections[subsection_index]
							? sections[subsection_index].student_list
							: [];
						return total_students + subsection_student_list.length;
					}, student_list.length);
		}, [sectionInfo, sections]);

    const cycleCourseIds = useMemo(() => {
      return sections.reduce((results, section) => {
        if (section?.course_id && sectionHasCycleQuarterDays(section)) results.add(`${section.course_id}`);
        return results;
      }, new Set());
    }, [sections]);

    const getDisplayQuartersForSectionInfo = useCallback((section_info = {}) => {
      return section_info?.course_id && cycleCourseIds.has(`${section_info.course_id}`)
        ? CYCLE_QUARTERS
        : QUARTERS;
    }, [cycleCourseIds]);

		const quarterDayInfo = useMemo(() => {
			const quarter_days = getDisplayQuarterDaysForSection(sectionInfo.section_id, sectionInfo.quarter_days);
			const quarter_display_info = getQuarterDisplayInfo(quarter_days, getDisplayQuartersForSectionInfo(sectionInfo));
			const unique_days = quarter_days?.length > 0
				? [...new Set(quarter_days.map(qd => `${qd.day}`))].sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
				: [];
			
			return {
				...quarter_display_info,
				uniqueDays: unique_days,
			};
		}, [getDisplayQuarterDaysForSection, getDisplayQuartersForSectionInfo, sectionInfo]);

    const individualQuarterRowsBySectionId = useMemo(() => {
      const main_quarter_days = getDisplayQuarterDaysForSection(sectionInfo.section_id, sectionInfo.quarter_days);
      const rows = [
        {
          sectionId: `${sectionInfo.section_id}`,
          quarterInfo: getQuarterDisplayInfo(main_quarter_days, getDisplayQuartersForSectionInfo(sectionInfo)),
          isMain: true,
        },
      ];

      (Array.isArray(sectionInfo.subsections) ? sectionInfo.subsections : []).forEach((subsection_id) => {
        const subsection_info = sections.find((section) => `${section.section_id}` === `${subsection_id}`);
        if (!subsection_info) return;

        rows.push({
          sectionId: `${subsection_id}`,
          quarterInfo: getQuarterDisplayInfo(getDisplayQuarterDaysForSection(subsection_id, subsection_info.quarter_days), getDisplayQuartersForSectionInfo(subsection_info)),
          isMain: false,
        });
      });

      const unique_quarter_keys = new Set(rows.map((row) => row.quarterInfo.quarterKey));
      if (rows.length <= 1 || unique_quarter_keys.size <= 1) return new Map();

      return rows.reduce((results, row) => {
        results.set(row.sectionId, row);
        return results;
      }, new Map());
    }, [getDisplayQuarterDaysForSection, getDisplayQuartersForSectionInfo, sectionInfo, sections]);

    const showIndividualQuarterRows = individualQuarterRowsBySectionId.size > 0;

    const expectedCoursePeriodDays = useMemo(() => {
      const section_course_period_ids = new Set(
        (Array.isArray(sectionInfo.course_periods) ? sectionInfo.course_periods : [])
          .filter(Boolean)
          .map((course_period_id) => `${course_period_id}`)
      );
      if (section_course_period_ids.size === 0) return [];

      const schedule_structure = scheduleStructureData?.schedule_structure || {};
      return Object.keys(schedule_structure)
        .filter((day) => {
          const day_schedule_periods = Array.isArray(schedule_structure[day])
            ? schedule_structure[day]
            : Object.values(schedule_structure[day] || {});

          return day_schedule_periods.some((schedule_structure_row) => {
            const course_period_id = typeof schedule_structure_row === "object"
              ? schedule_structure_row?.course_period_id
              : schedule_structure_row;
            return section_course_period_ids.has(`${course_period_id}`);
          });
        })
        .map((day) => `${day}`)
        .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    }, [scheduleStructureData, sectionInfo.course_periods]);

    const showSectionDays = useMemo(() => {
      if (expectedCoursePeriodDays.length === 0) return false;
      if (quarterDayInfo.uniqueDays.length !== expectedCoursePeriodDays.length) return true;

      const expected_days = new Set(expectedCoursePeriodDays);
      return quarterDayInfo.uniqueDays.some((day) => !expected_days.has(`${day}`));
    }, [expectedCoursePeriodDays, quarterDayInfo.uniqueDays]);

		const singletonDoubletonInfo = useMemo(() => getSectionSingletonDoubletonInfo({
			sectionId: section_id,
			sections,
			subsections,
			separateICRCodes: scheduleInfo.separate_ICR_codes,
		}), [sections, subsections, scheduleInfo.separate_ICR_codes, section_id]);

		const labelsInfo = useMemo(() => {
			let section_labels = sectionInfo && "labels" in sectionInfo ? sectionInfo.labels : [];
			section_labels = section_labels.map((label_id) => {
				const label_info = labels.find((label) => "label_id" in label && label.label_id === label_id);
				return label_info;
			});

			const sorted_teams = [];
			const current_subsection_labels = section_labels.filter((label) => label);
			const sorted_labels = sortArrayOfObjects(current_subsection_labels, "label", "text", "asc");

			return { sortedTeams: sorted_teams, sortedLabels: sorted_labels };
		}, [sectionInfo, labels]);

    const showSectionConflictIndicators = magnetboardOptions.show_section_conflict_indicators !== false;
    const allowInlineQuarterEditing = magnetboardOptions.allow_inline_quarter_editing !== false;
    const allowLockedSectionRosterEditing = magnetboardOptions.allow_locked_section_roster_editing === true;
    const allowSectionDuplicate = magnetboardOptions.allow_section_duplicate !== false;
    const allowSectionDelete = magnetboardOptions.allow_section_delete !== false;

		const conflictsAndColor = useMemo(() => {
      const default_section_color =
        currentView === "student" ? ""
        : sectionInfo.is_lab === "1" ? "green"
        : sectionInfo.section_type === "2" ? "pastel-light-blue"
        : sectionInfo.section_type === "3" ? "pastel-brown"
        : "dark-blue";

      if (!showSectionConflictIndicators) {
        return {
          applicableConflicts: [],
          sectionColor: default_section_color,
          hasHardConflict: false,
          hasWarning: false,
        };
      }

      if (currentView === "student") {
        const has_hard_conflict = !!studentConflictSectionLookup?.has(`${studentID}::${sectionID}`);

        return {
          applicableConflicts: [],
          sectionColor: "",
          hasHardConflict: has_hard_conflict,
          hasWarning: false,
        };
      }

			const relevant_section_ids = new Set(
				(affectedSectionPartIds.length > 0 ? affectedSectionPartIds : [sectionID])
					.map((affected_section_id) => `${affected_section_id}`)
			);

      let has_hard_conflict = false;
      let has_warning = false;

      relevant_section_ids.forEach((relevant_section_id) => {
        const summary = sectionConflictSummaryBySectionId?.[`${relevant_section_id}`];
        if (!summary) return;

        if (summary.hasHardConflict) has_hard_conflict = true;
        if (summary.hasWarning) has_warning = true;
      });

      has_warning = !has_hard_conflict && has_warning;
			const section_color = 
				currentView === "student" ? ""
				: has_hard_conflict ? "red"
				: has_warning ? "orange"
				: sectionInfo.is_lab === "1" ? "green"
				: sectionInfo.section_type === "2" ? "pastel-light-blue" 
				: sectionInfo.section_type === "3" ? "pastel-brown"
				: "dark-blue";

			return {
        applicableConflicts: [],
        sectionColor: section_color,
        hasHardConflict: has_hard_conflict,
        hasWarning: has_warning,
      };
			}, [affectedSectionPartIds, sectionConflictSummaryBySectionId, sectionID, currentView, sectionInfo.is_lab, sectionInfo.section_type, showSectionConflictIndicators, studentConflictSectionLookup, studentID]);

		const isSectionSelected = useMemo(() => {
			const selected_section_info = sections.find((section) => section.section_id === selectedQuickViewSectionID);
			const selected_section_span_id = selected_section_info ? selected_section_info.span_id : null;
			const span_id = sectionInfo.span_id;

			return selectedQuickViewSectionID &&
				(section_id === selectedQuickViewSectionID || (span_id && span_id === selected_section_span_id));
		}, [sections, selectedQuickViewSectionID, sectionInfo.span_id, section_id]);

		const computedData = useMemo(() => {
			// Early return for lab filtering
			if (
				sectionInfo.is_lab === "1" &&
				scheduleType === "block" &&
				currentViewType === "day" &&
				quarterDayInfo.uniqueDays.length > 0 &&
				quarterDayInfo.uniqueDays.findIndex((day) => day === currentDay) === -1
			) {
				return null;
			}

			return {
				sectionInfo: sectionInfo,
				courseName: courseAndClassroomInfo.course_name,
				classroomName: courseAndClassroomInfo.classroom_name,
				numStudents: studentCount,
				uniqueDays: quarterDayInfo.uniqueDays,
        showSectionDays,
				sortedTeams: labelsInfo.sortedTeams,
				sortedLabels: labelsInfo.sortedLabels,
				// applicableConflicts: conflictsAndColor.applicableConflicts,
				isSingleton: singletonDoubletonInfo.isSingleton,
				isDoubleton: singletonDoubletonInfo.isDoubleton,
				// sectionColor: conflictsAndColor.sectionColor,
				isSectionSelected: isSectionSelected,
				teacherInfo: teacherInfo,
			}
		}, [
			sectionInfo,
			courseAndClassroomInfo,
			studentCount,
			quarterDayInfo,
      showSectionDays,
			labelsInfo,
			// conflictsAndColor,
			singletonDoubletonInfo,
			isSectionSelected,
			teacherInfo,
			scheduleType,
			currentViewType,
			currentDay,
		]);

    const dragRestrictionInfo = useMemo(() => {
      const relatedSectionIDs = new Set([
        `${section_id}`,
        ...(Array.isArray(sectionInfo.subsections)
          ? sectionInfo.subsections.map((subsection_id) => `${subsection_id}`)
          : []),
      ]);

      const relatedTeacherIDs = new Set();
      const relatedCourseIDs = new Set();

      relatedSectionIDs.forEach((relatedSectionID) => {
        const relatedSectionInfo = sections.find(
          (section) => `${section.section_id}` === `${relatedSectionID}`
        );
        if (!relatedSectionInfo) return;

        if (relatedSectionInfo.teacher_id) {
          relatedTeacherIDs.add(`${relatedSectionInfo.teacher_id}`);
        }

        if (Array.isArray(relatedSectionInfo.secondary_teachers)) {
          relatedSectionInfo.secondary_teachers.forEach((teacher_id) => {
            if (teacher_id) relatedTeacherIDs.add(`${teacher_id}`);
          });
        }

        if (relatedSectionInfo.course_id) {
          relatedCourseIDs.add(`${relatedSectionInfo.course_id}`);
        }
      });

      const teacherRestrictedPeriods = new Set();
      const courseRestrictedPeriods = new Set();

      relatedTeacherIDs.forEach((teacher_id) => {
        const teacherInfo = teachers.find(
          (teacher) => `${teacher.teacher_id}` === `${teacher_id}`
        );
        const periodRestrictions = Array.isArray(teacherInfo?.period_restrictions)
          ? teacherInfo.period_restrictions
          : [];
        periodRestrictions.forEach((restricted_course_period_id) => {
          teacherRestrictedPeriods.add(`${restricted_course_period_id}`);
        });
      });

      relatedCourseIDs.forEach((course_id) => {
        const courseInfo = courses.find(
          (course) => `${course.course_id}` === `${course_id}`
        );
        const periodRestrictions = Array.isArray(courseInfo?.period_restrictions)
          ? courseInfo.period_restrictions
          : [];
        periodRestrictions.forEach((restricted_course_period_id) => {
          courseRestrictedPeriods.add(`${restricted_course_period_id}`);
        });
      });

      return {
        teacherRestrictedPeriods: Array.from(teacherRestrictedPeriods),
        courseRestrictedPeriods: Array.from(courseRestrictedPeriods),
      };
    }, [section_id, sectionInfo.subsections, sections, teachers, courses]);

		// /////////////////////////
    // ///// DRAG FUNCTION /////
    // /////////////////////////
    const [, sectionRef] = useDrag(() => ({
      type: sectionType,
      item: {
        draggedDataID: dataID,
        draggedCoursePeriodID: coursePeriodID,
        sectionID: section_id,
        type: sectionType,
        teacherRestrictedPeriods: dragRestrictionInfo.teacherRestrictedPeriods,
        courseRestrictedPeriods: dragRestrictionInfo.courseRestrictedPeriods,
      },
      canDrag: () => !!isDraggable,
      collect: (monitor) => ({}),
    }), [
      coursePeriodID,
      dataID,
      dragRestrictionInfo.courseRestrictedPeriods,
      dragRestrictionInfo.teacherRestrictedPeriods,
      isDraggable,
      sectionType,
      section_id,
    ]);

    const hide_section = useMemo(() =>!(
      (!labsOnlyFiltered &&
        !nonLabsOnlyFiltered &&
        !singletonsFiltered &&
        !doubletonsFiltered &&
        !hasClassroomFiltered &&
        !noClassroomFiltered) ||
      (labsOnlyFiltered && sectionInfo.is_lab === "1") ||
      (nonLabsOnlyFiltered && sectionInfo.is_lab !== "1") ||
      (singletonsFiltered && singletonDoubletonInfo.isSingleton) ||
      (doubletonsFiltered && singletonDoubletonInfo.isDoubleton) ||
      (hasClassroomFiltered &&
        sectionInfo.classroom_id &&
        sectionInfo.course_periods.length > 0) ||
      (noClassroomFiltered &&
        !sectionInfo.classroom_id &&
        sectionInfo.course_periods.length > 0)
    ), [sectionInfo, labsOnlyFiltered, nonLabsOnlyFiltered, singletonsFiltered, doubletonsFiltered, hasClassroomFiltered, noClassroomFiltered, singletonDoubletonInfo.isSingleton, singletonDoubletonInfo.isDoubleton]);

    const teacherDisplayInfo = useMemo(() => {
      const section_part_infos = [
        sectionInfo,
        ...(Array.isArray(sectionInfo.subsections) ? sectionInfo.subsections : [])
          .map((subsection_id) => sections.find((section) => `${section.section_id}` === `${subsection_id}`))
          .filter(Boolean),
      ].filter((section_part_info) => section_part_info?.section_id);

      const teacher_name_by_section_id = section_part_infos.reduce((results, section_part_info) => {
        const current_teacher_info = teachers.find(
          (teacher) => `${teacher.teacher_id}` === `${section_part_info.teacher_id}`
        );
        if (current_teacher_info?.name && current_teacher_info?.first_name) {
          results[`${section_part_info.section_id}`] = `${current_teacher_info.name}, ${current_teacher_info.first_name}`;
        }
        return results;
      }, {});

      const assigned_student_section_ids = section_part_infos.reduce((results, section_part_info) => {
        const student_list = Array.isArray(section_part_info.student_list) ? section_part_info.student_list : [];
        if (student_list.some((section_student_id) => `${section_student_id}` === `${studentID}`)) {
          results.add(`${section_part_info.section_id}`);
        }
        return results;
      }, new Set());

      const unique_teacher_ids = new Set(
        section_part_infos
          .map((section_part_info) => `${section_part_info.teacher_id || ""}`)
          .filter((teacher_id) => teacher_id !== "")
      );
      const all_parts_have_teacher = section_part_infos.every((section_part_info) => section_part_info.teacher_id);
      const show_teacher_breakout_view = ["teacher", "classroom", "course"].includes(currentView);
      const same_teacher_for_all_parts = section_part_infos.length > 0 && all_parts_have_teacher && unique_teacher_ids.size === 1;
      const common_teacher_name = same_teacher_for_all_parts
        ? teacher_name_by_section_id[`${sectionInfo.section_id}`]
        : null;

      return {
        assignedStudentSectionIds: assigned_student_section_ids,
        commonTeacherName: common_teacher_name,
        showCommonTeacherName: show_teacher_breakout_view && !!common_teacher_name,
        showPartTeacherNames: show_teacher_breakout_view && !same_teacher_for_all_parts,
        teacherNameBySectionId: teacher_name_by_section_id,
      };
    }, [currentView, sectionInfo, sections, teachers, studentID]);

		/// Set Display ///
		// Early return if section should be hidden due to lab filtering
		if (!computedData) return null;

		const { courseName, classroomName, numStudents, uniqueDays, showSectionDays: displaySectionDays, sortedTeams, sortedLabels } = computedData;

    const showConflictsWarnings = !printDisplayOptions || printDisplayOptions.showConflictsWarnings !== false;
    const sectionColor = showConflictsWarnings ? conflictsAndColor.sectionColor : "";
    const hasHardConflict = showConflictsWarnings && conflictsAndColor.hasHardConflict;
    const hasWarning = showConflictsWarnings && conflictsAndColor.hasWarning;
    const hasLockedVisual = currentView !== "student" && allSectionPartsLocked && !hasHardConflict && !hasWarning;
    const canOpenSectionEditor = isEditable && (!allSectionPartsLocked || allowLockedSectionRosterEditing);
    const showSectionActions = canOpenSectionEditor && !isPrintMode;
    const showClassroomDetail = !printDisplayOptions || printDisplayOptions.showClassroom === true;
    const showStudentCountDetail = !printDisplayOptions || printDisplayOptions.showStudentCount === true;
    const canChangeClassroom =
      isClassroomEditable &&
      !allSectionPartsLocked &&
      !isPrintMode &&
      typeof updateSectionPlacement === "function";
    const showSectionDetailRow = sectionInfo.section_type === "1" && (
      printDisplayOptions
        ? (showClassroomDetail || showStudentCountDetail)
        : !isPrintMode
    );
    const handleClassroomSelect = async (event, classroom_id) => {
      event.preventDefault();
      event.stopPropagation();
      if (!canChangeClassroom || classroomSaving) return;

      if (`${sectionInfo.classroom_id || ""}` === `${classroom_id || ""}`) {
        setClassroomMenuOpen(false);
        return;
      }

      setClassroomSaving(true);
      setClassroomMenuOpen(false);

      try {
        await updateSectionPlacement(
          {
            draggedDataID: sectionInfo.classroom_id,
            droppedDataID: classroom_id,
            draggedCoursePeriodID: coursePeriodID,
            droppedCoursePeriodID: coursePeriodID,
            sectionID: sectionInfo.section_id,
            type: sectionType,
          },
          { viewOverride: "classroom" }
        );
      } finally {
        if (componentMountedRef.current) setClassroomSaving(false);
      }
    };
    const renderClassroomOption = (option) => {
      const classroom_info = option.classroomInfo;
      const classroom_is_selected =
        `${sectionInfo.classroom_id || ""}` === `${classroom_info.classroom_id || ""}`;
      const connection_labels = [
        option.attachedToCourse ? "Course" : null,
        option.attachedToTeacher ? "Teacher" : null,
      ].filter(Boolean);

      return (
        <button
          type="button"
          className={`mb-section-classroom-option ${classroom_is_selected ? "mb-section-classroom-option-selected" : ""}`}
          disabled={classroomSaving || classroom_is_selected}
          onClick={classroom_is_selected ? undefined : (event) => handleClassroomSelect(event, classroom_info.classroom_id)}
          key={classroom_info.classroom_id}
        >
          <span className="mb-section-classroom-option-name">
            <span>{classroom_info.classroom_name || "Unnamed classroom"}</span>
            {connection_labels.length > 0 && (
              <span className="mb-section-classroom-option-connections">
                {connection_labels.join(" + ")}
              </span>
            )}
          </span>
          <span className={classroom_is_selected
            ? "mb-section-classroom-current"
            : `mb-section-classroom-availability ${option.available ? "available" : "unavailable"}`
          }>
            {classroom_is_selected
              ? "Current classroom"
              : (option.available ? "Available" : "Unavailable")
            }
          </span>
          {classroom_is_selected && (
            <FontAwesomeIcon className="mb-section-classroom-selected-icon" icon={faCheck} />
          )}
        </button>
      );
    };
    const renderTeacherLine = (part_section_info) => {
      if (!part_section_info?.section_id) return null;
      const part_section_id = `${part_section_info.section_id}`;
      const teacher_name = teacherDisplayInfo.teacherNameBySectionId[part_section_id];
      if (!teacher_name) return null;

      const should_show_teacher = currentView === "student"
        ? teacherDisplayInfo.assignedStudentSectionIds.has(part_section_id)
        : teacherDisplayInfo.showPartTeacherNames;

      if (!should_show_teacher) return null;
      return <div className="mb-section-teacher-name">{teacher_name}</div>;
    };
    const duplicateLoadingTargetsForSection = (() => {
      const target_keys = new Set();

      affectedMainSections.forEach((affected_section_info) => {
        const course_periods = Array.isArray(affected_section_info.course_periods)
          ? affected_section_info.course_periods
          : [];
        if (course_periods.length === 0) return;

        const row_data_ids =
          currentView === "teacher"
            ? [
                affected_section_info.teacher_id,
                ...(Array.isArray(affected_section_info.secondary_teachers)
                  ? affected_section_info.secondary_teachers
                  : []),
              ]
            : currentView === "course"
            ? [affected_section_info.course_id]
            : currentView === "classroom"
            ? [affected_section_info.classroom_id]
            : [];

        row_data_ids.filter(Boolean).forEach((row_data_id) => {
          course_periods.filter(Boolean).forEach((course_period_id) => {
            target_keys.add(`${row_data_id}::${course_period_id}`);
          });
        });
      });

      return Array.from(target_keys).map((target_key) => {
        const [target_data_id, target_course_period_id] = target_key.split("::");
        return { dataID: target_data_id, coursePeriodID: target_course_period_id };
      });
    })();
    const deleteLoadingSectionIDsForSection = affectedMainSections.map(
      (affected_section_info) => `${affected_section_info.section_id}`
    );
    const isDeletingSection = !!deleteLoadingTargets?.[`${sectionInfo.section_id}`];

    const handleEditSectionFromMenu = (event) => {
      event.stopPropagation();
      setSectionActionsOpen(false);
      toggleEditSection({
        section_id: sectionInfo.section_id,
        data_id: dataID,
        section_type: sectionInfo.section_type,
        course_period_id: coursePeriodID,
      });
    };

    const handleDuplicateSectionFromMenu = async (event) => {
      event.stopPropagation();
      setSectionActionsOpen(false);
      if (typeof duplicateSectionFromMagnetboard === "function") {
        const copy_periods = Array.isArray(sectionInfo.course_periods) && sectionInfo.course_periods.length > 0;
        const has_target_periods =
          duplicateLoadingTargetsForSection.length > 0 &&
          typeof setDuplicateLoadingTarget === "function";

        if (has_target_periods) {
          duplicateLoadingTargetsForSection.forEach((target) => {
            setDuplicateLoadingTarget(target.dataID, target.coursePeriodID, true);
          });
        } else {
          setIsDuplicatingSection(true);
        }

        try {
          await duplicateSectionFromMagnetboard(sectionInfo.section_id, { copyPeriods: copy_periods });
        } finally {
          if (has_target_periods) {
            duplicateLoadingTargetsForSection.forEach((target) => {
              setDuplicateLoadingTarget(target.dataID, target.coursePeriodID, false);
            });
          } else {
            setIsDuplicatingSection(false);
          }
        }
      }
    };

    const handleQuarterDayPress = (event, quarter, target_section_id, allow_separate_subsection_update = false) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.nativeEvent?.stopImmediatePropagation) {
        event.nativeEvent.stopImmediatePropagation();
      }

      flushSync(() => {
        markQuarterLoading(target_section_id, quarter);
      });

      window.requestAnimationFrame(() => {
        toggleSectionQuarterDay(target_section_id, quarter, { allow_separate_subsection_update });
      });
    };

    const handleDeleteSectionFromMenu = async (event) => {
      event.stopPropagation();
      setSectionActionsOpen(false);
      if (typeof deleteSectionFromMagnetboard !== "function") return;

      const delete_section_bool = await swal({
        title: "Delete Section?",
        icon: "warning",
        buttons: {
          cancel: {
            text: "Cancel",
            value: false,
            visible: true,
            className: "cancel-btn",
          },
          confirm: {
            text: "Yes, Delete",
            value: true,
            visible: true,
            className: "red-btn",
            closeModal: false,
          },
        },
        content: (
          <div>
            <p>Do you really want to delete this section? This will remove the section, its students, and any connected spanning sections.</p>
          </div>
        ),
      });

      if (!delete_section_bool) return;

      if (typeof setDeleteLoadingTarget === "function") {
        setDeleteLoadingTarget(deleteLoadingSectionIDsForSection, true);
      }
      try {
        await deleteSectionFromMagnetboard(sectionInfo.section_id);
      } finally {
        if (typeof setDeleteLoadingTarget === "function") {
          setDeleteLoadingTarget(deleteLoadingSectionIDsForSection, false);
        }
        swal.close();
      }
    };

    const renderQuarterDayBar = (quarter_info, {
      interactive = false,
      compact = false,
      targetSectionId = sectionInfo.section_id,
      allowSeparateSubsectionUpdate = false,
    } = {}) => {
      const display_quarters = Array.isArray(quarter_info.displayQuarters) && quarter_info.displayQuarters.length > 0
        ? quarter_info.displayQuarters
        : QUARTERS;
      const quarter_selected_lookup = quarter_info.quarterSelectedLookup || {
        "1": quarter_info.quarter1Bool,
        "2": quarter_info.quarter2Bool,
        "3": quarter_info.quarter3Bool,
        "4": quarter_info.quarter4Bool,
        "5": quarter_info.quarter5Bool,
        "6": quarter_info.quarter6Bool,
      };
      const quarter_label_prefix = quarter_info.quarterLabelPrefix || (display_quarters.length > 4 ? "C" : "Q");

      return (
        <div className={`mb-section-quarter-day-container ${display_quarters.length > 4 ? "mb-section-quarter-day-container-cycle" : ""} ${compact ? "mb-section-quarter-day-container-compact" : ""} ${!interactive ? "mb-section-quarter-day-container-readonly" : ""}`}>
          {display_quarters.map((quarter) => (
            <div
              className={`mb-section-quarter-day ${quarter_selected_lookup[quarter] ? "mb-section-quarter-day-selected" : ""}`}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              onMouseDownCapture={interactive ? (event) => handleQuarterDayPress(event, quarter, targetSectionId, allowSeparateSubsectionUpdate) : undefined}
              onTouchStartCapture={interactive ? (event) => handleQuarterDayPress(event, quarter, targetSectionId, allowSeparateSubsectionUpdate) : undefined}
              key={`quarter-${quarter}`}
            >
              {interactive && quarterLoadingKeys.includes(getQuarterLoadingKey(targetSectionId, quarter)) ? (
                <img className="mb-section-quarter-day-loader" src={loading_gif} alt="loading..." draggable={false} />
              ) : (
                `${quarter_label_prefix}${quarter}`
              )}
            </div>
          ))}
        </div>
      );
    };

    if (!sectionInfo.section_id) return null;

    return (
      <div
        className={currentView === "student" ? "mb-student-section" : `mb-section ${allSectionPartsLocked ? "mb-section-locked" : "" } ${isSectionSelected ? "mb-highlight-section" : "" } ${hide_section ? "hide" : "" }`}
        key={`${coursePeriodID}-${sectionInfo.section_id}`}
        data-sectionid={sectionInfo.section_id}
        onDoubleClick={
          canOpenSectionEditor
            ? () =>
                toggleEditSection({
                  section_id: sectionInfo.section_id,
                  data_id: dataID,
                  section_type: sectionInfo.section_type,
                  course_period_id: coursePeriodID,
                })
            : () => {}
        }
        ref={sectionRef}
      >
        {((isLockSectionLoading && Array.isArray(isLockSectionIds) && isLockSectionIds.includes(sectionInfo.section_id)) || isDeletingSection || isDuplicatingSection) && (
          <div className="mb-section-loader">
            {isDeletingSection || isDuplicatingSection ? (
              <img className="mb-section-balls-loader" src={loading_gif} alt="loading..." />
            ) : (
              <Loader size="48px" />
            )}
          </div>
        )}

        <div className={`mb-section-top-bar ${sectionColor} ${sectionInfo.is_manual === "1" ? "mb-section-top-bar-manual" : ""}`}>
          {isLockable && !isPrintMode && <div className="lock-section-btn">
            {sectionInfo.is_lab === "1" ? (
              "Lab"
            ) : allSectionPartsLocked ? (
              <FontAwesomeIcon
                className="cursor-pointer bright-yellow-text"
                icon={faLock}
                onClick={() => toggleLockSection([sectionInfo.section_id], "0")}
              />
            ) : (
              <FontAwesomeIcon
                className="opaque-link"
                icon={faUnlock}
                onClick={() => toggleLockSection([sectionInfo.section_id], "1")}
              />
            )}
          </div>}
          {showSectionActions ? (
            <div
              className="mb-section-more-info-container"
              ref={sectionActionsRef}
              onClick={(event) => event.stopPropagation()}
            >
              <div
                className="mb-section-more-info"
                onClick={(event) => {
                  event.stopPropagation();
                  setSectionActionsOpen((current_value) => !current_value);
                }}
              >
                <FontAwesomeIcon icon={faEllipsisH} />
              </div>
              {sectionActionsOpen && (
                <div className="mb-section-actions-dropdown">
                  <div className="mb-section-actions-dropdown-option" onClick={handleEditSectionFromMenu}>
                    Edit Section
                  </div>
                  {allowSectionDuplicate && <div className="mb-section-actions-dropdown-option" onClick={handleDuplicateSectionFromMenu}>
                    Duplicate Section
                  </div>}
                  {allowSectionDelete && <div className="mb-section-actions-dropdown-option mb-section-actions-dropdown-option-delete" onClick={handleDeleteSectionFromMenu}>
                    Delete Section
                  </div>}
                </div>
              )}
            </div>
          ) : null }
        </div>
        <div
          className={`mb-section-content ${
            hasHardConflict
              ? "mb-section-conflict"
              : hasWarning
              ? "mb-section-warning"
              : ""
          } ${sectionInfo.is_manual === "1" ? "mb-section-manual" : ""} ${
            sectionInfo.is_manual === "1" && sectionInfo.is_lab === "1"
              ? "mb-section-manual-lab"
              : ""
          } ${hasLockedVisual ? "mb-section-locked-visual" : ""}`}
          onClick={(e) =>
            isQuickViewEnabled &&
            typeof handleSelectQuickViewDataID === "function"
              ? handleSelectQuickViewDataID(dataID, sectionInfo.section_id, e)
              : undefined
          }
        >
          <div className="tooltip" data-tooltip-text={courseName}>
            <div className="mb-section-name-with-status">
              <div className={`mb-section-name ellipsis dark-blue-text`}>
                {courseName ? courseName : "..."}
              </div>
              {sectionInfo.locked === "1" && !allSectionPartsLocked && (
                <span className="mb-section-lock-status-badge" title="Main section locked">
                  <FontAwesomeIcon icon={faLock} />
                </span>
              )}
            </div>
          </div>
          {renderTeacherLine(sectionInfo)}
          {showIndividualQuarterRows && individualQuarterRowsBySectionId.has(`${sectionInfo.section_id}`) &&
            renderQuarterDayBar(individualQuarterRowsBySectionId.get(`${sectionInfo.section_id}`).quarterInfo, {
              interactive: allowInlineQuarterEditing && !isPrintMode && sectionInfo.locked !== "1",
              compact: true,
              targetSectionId: sectionInfo.section_id,
            })
          }
          {sortedTeams.map((label, index) => (
            <div
              className="mb-section-label extra-small-text"
              key={label.label_id}
            >
              <div className={`mb-section-label-icon ${getLabelColorClass(label)}`}></div>
              <div>{label.label}</div>
            </div>
          ))}
          {sortedLabels.map((label, index) => (
            <div
              className="mb-section-label extra-small-text"
              key={label.label_id}
            >
              <div className={`mb-section-label-icon ${getLabelColorClass(label)}`}></div>
              <div>{label.label}</div>
            </div>
          ))}
          {sectionInfo.subsections.map((subsection_id) => {
            const section_subsection_info = sections.find(
              (section) => section.section_id === subsection_id
            );
            const subsection_course_id = section_subsection_info
              ? section_subsection_info.course_id
              : null;
            const subsection_course_info = subsection_course_id
              ? courses.find(
                  (course) => course.course_id === subsection_course_id
                )
              : null;
            const subsection_course_name = subsection_course_info
              ? capitalizeFirstLetters(subsection_course_info.name)
              : null;
            const subsection_course_code = subsection_course_info
              ? subsection_course_info.course_code
              : null;

            // Check if subsection is placeholder inclusion course (for schools without separate ICR codes)
            const subsection_is_inclusion = section_subsection_info
              ? section_subsection_info.is_inclusion
              : "0";
            let is_placeholder_inclusion = false;
            if (
              scheduleInfo.separate_ICR_codes === "0" &&
              sectionInfo.course_id === subsection_course_id &&
              subsection_is_inclusion === "1"
            )
              is_placeholder_inclusion = true;
            const subsection_text_color =
              subsection_is_inclusion === "1" ? INCLUSION_SUBSECTION_TEXT_CLASS : "blue-text";

            let subsection_labels =
              section_subsection_info && "labels" in section_subsection_info
                ? section_subsection_info.labels
                : [];
            subsection_labels = subsection_labels.map((label_id) => {
              const label_info = labels.find(
                (label) => label.label_id === label_id
              );
              return label_info;
            });

            const sorted_subsection_teams = [];
            const current_subsection_labels = subsection_labels.filter(
              (label) => label
            );
            const sorted_subsection_labels = sortArrayOfObjects(
              current_subsection_labels,
              "label",
              "text",
              "asc"
            );

            return (
              <React.Fragment
                key={`${sectionInfo.section_id}-${subsection_id}`}
              >
                {is_placeholder_inclusion ? (
                  <>
                    <div className="mb-section-name-with-status">
                      <div className={INCLUSION_SUBSECTION_TEXT_CLASS}>Inclusion</div>
                      {section_subsection_info?.locked === "1" && !allSectionPartsLocked && (
                        <span className="mb-section-lock-status-badge" title="Subsection locked">
                          <FontAwesomeIcon icon={faLock} />
                        </span>
                      )}
                    </div>
                    {renderTeacherLine(section_subsection_info)}
                    {showIndividualQuarterRows && individualQuarterRowsBySectionId.has(`${subsection_id}`) &&
                      renderQuarterDayBar(individualQuarterRowsBySectionId.get(`${subsection_id}`).quarterInfo, {
                        interactive: allowInlineQuarterEditing && !isPrintMode && section_subsection_info?.locked !== "1",
                        compact: true,
                        targetSectionId: subsection_id,
                        allowSeparateSubsectionUpdate: true,
                      })
                    }
                  </>
                ) : (
                  <>
                    {subsection_course_id && (
                      <div
                        className="tooltip"
                        data-tooltip-text={`(${subsection_course_code}) ${
                          subsection_course_name &&
                          capitalizeFirstLetters(subsection_course_name)
                        }`}
                      >
                        <div className="mb-section-name-with-status">
                          <div className={`mb-section-name ellipsis ${subsection_text_color}`}>
                            {subsection_course_name &&
                              capitalizeFirstLetters(subsection_course_name)}{" "}
                            {subsection_course_code &&
                              `(${subsection_course_code})`}
                          </div>
                          {section_subsection_info?.locked === "1" && !allSectionPartsLocked && (
                            <span className="mb-section-lock-status-badge" title="Subsection locked">
                              <FontAwesomeIcon icon={faLock} />
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {renderTeacherLine(section_subsection_info)}
                    {showIndividualQuarterRows && individualQuarterRowsBySectionId.has(`${subsection_id}`) &&
                      renderQuarterDayBar(individualQuarterRowsBySectionId.get(`${subsection_id}`).quarterInfo, {
                        interactive: allowInlineQuarterEditing && !isPrintMode && section_subsection_info?.locked !== "1",
                        compact: true,
                        targetSectionId: subsection_id,
                        allowSeparateSubsectionUpdate: true,
                      })
                    }
                    {sorted_subsection_teams.map((label, index) => (
                      <div
                        className="mb-section-label extra-small-text"
                        key={label.label_id}
                      >
                        <div className={`mb-section-label-icon ${getLabelColorClass(label)}`}></div>
                        <div className={subsection_text_color}>{label.label}</div>
                      </div>
                    ))}
                    {sorted_subsection_labels.map((label, index) => (
                      <div
                        className="mb-section-label extra-small-text"
                        key={label.label_id}
                      >
                        <div className={`mb-section-label-icon ${getLabelColorClass(label)}`}></div>
                        <div className={subsection_text_color}>{label.label}</div>
                      </div>
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
          {teacherDisplayInfo.showCommonTeacherName ? (
            <div className="mb-section-teacher-name">
              {teacherDisplayInfo.commonTeacherName}
            </div>
          ) : (
            currentView !== "teacher" &&
            currentView !== "student" &&
            !teacherDisplayInfo.showPartTeacherNames &&
            teacherInfo &&
            teacherInfo.name &&
            teacherInfo.first_name && (
              <div className="mb-section-teacher-name">
                {teacherInfo.name}, {teacherInfo.first_name}
              </div>
            )
          )}
          {!isPrintMode && !showIndividualQuarterRows && renderQuarterDayBar(quarterDayInfo, { interactive: allowInlineQuarterEditing && !allSectionPartsLocked, compact: true })}
          {!isPrintMode && displaySectionDays && (sectionInfo.is_lab === "1" ? (
            <div className="mb-lab-days extra-small-text green-text">
              {uniqueDays.length > 0
                ? `Day(s) ${uniqueDays.map((day) => day)}`
                : "No lab days"}
            </div>
          ) : scheduleType === "daily" ? (
            <div className="mb-lab-days extra-small-text blue-text">
              {uniqueDays.length > 0 ? `Day(s) ${uniqueDays.map((day) => day)}` : "No days"}
            </div>
          ) : null)}
          {showSectionDetailRow && (
            <div className="mb-section-subname-container extra-small-text">
              {showClassroomDetail && (
                canChangeClassroom ? (
                  <div
                    className="mb-section-classroom-selector"
                    ref={classroomMenuRef}
                    onClick={(event) => event.stopPropagation()}
                    onDoubleClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                  >
                    <button
                      type="button"
                      className="mb-section-subname mb-section-classroom-trigger"
                      aria-expanded={classroomMenuOpen}
                      aria-label={`Change classroom${classroomName ? ` from ${classroomName}` : ""}`}
                      disabled={classroomSaving}
                      onClick={(event) => {
                        event.stopPropagation();
                        setSectionActionsOpen(false);
                        setClassroomMenuOpen((current_value) => !current_value);
                      }}
                    >
                      <FontAwesomeIcon icon={faChalkboard} />
                      <span>{classroomSaving ? "Saving..." : (classroomName || "--")}</span>
                    </button>
                    {classroomMenuOpen && (
                      <div className="mb-section-classroom-menu">
                        <div className="mb-section-classroom-menu-title">Change classroom</div>
                        <div className="mb-section-classroom-options">
                          {classroomOptions.attached.length > 0 && (
                            <>
                              <div className="mb-section-classroom-group-label">
                                Course &amp; teacher classrooms
                              </div>
                              {classroomOptions.attached.map(renderClassroomOption)}
                            </>
                          )}
                          {classroomOptions.other.length > 0 && (
                            <>
                              <div className="mb-section-classroom-group-label">
                                {classroomOptions.attached.length > 0 ? "Other classrooms" : "All classrooms"}
                              </div>
                              {classroomOptions.other.map(renderClassroomOption)}
                            </>
                          )}
                          {classroomOptions.attached.length === 0 && classroomOptions.other.length === 0 && (
                            <div className="mb-section-classroom-empty">No classrooms found.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-section-subname">
                    <FontAwesomeIcon icon={faChalkboard} />
                    {classroomName ? classroomName : "--"}
                  </div>
                )
              )}
              {showStudentCountDetail && (
                <div className="mb-section-students">
                  <FontAwesomeIcon icon={faUserGraduate} /> {numStudents}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default MagnetboardSection;
